import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import {
  CUSTOM_FILTER_CHIP_ID,
  type IFilterData,
  type IHeader,
} from "../../components/Organismos/Table/FilteredTable.types";
import {
  formatEventLookupLabel,
  getEventLookupOptions,
  type EventLookupOption,
} from "../../Services/Event";
import { getAllFirms } from "../../Services/Firm";
import { subscribeEmailUnsubscriberItem } from "../../Services/EmailUnsubscriber";
import IFirm from "../../interfaces/models/IFirm";
import { EmailTypeMemberEnum } from "../../Enums/EmailEnum";
import { formatDateToString } from "../../utils/functions";
import Button from "../../components/staff/Button";
import NewButton from "../../components/Atomos/Buttons/newButton";
import Modal from "../../components/Atomos/Modals/Modal";
import CloseModal from "../../components/Atomos/Icons/EditMode/CloseModal";
import TemplateDateFlightsPicker from "../SendEmail/TemplateDateFlightsPicker";
import {
  buildTemplateCreatedDateFilter,
  summarizeTemplateDateFilterSelection,
} from "../SendEmail/templateDateFilter";
import {
  DEFAULT_TEMPLATE_DATE_FILTER,
  type TemplateDateFilterValue,
} from "../../utils/templateDateFilterTypes";
import "../EventsPage/Events.css";

interface UnsubscribedExpandedRow {
  id: string;
  recordId: string;
  itemKind: "category" | "event";
  itemValue: string;
  itemLabel: string;
  groupIndex: number;
  personId?: string;
  personName?: string;
  personTitle?: string;
  personFirm?: string;
  personFirmName?: string;
  personShortId?: string;
  contactId?: string;
  emailMeeting?: string;
  unsubscribedDateStr?: string;
  unsubscribedAt?: string;
}

const endpoint = "/emailUnsubscriber";
// Previous key stored the retired date-preset multiselect; that shape breaks the range picker.
const TABLE_KEY = "unsubscribed-list-date-range";

function unsubscribeDateFilterChips (filterValue: unknown) {
  const label = summarizeTemplateDateFilterSelection(
    filterValue as TemplateDateFilterValue | null | undefined,
  );
  if (!label) {
    return [];
  }
  return [{ id: CUSTOM_FILTER_CHIP_ID, name: label }];
}

function unsubscribeDateClause (
  value: TemplateDateFilterValue,
): { value: string; comparisonType: "BETWEEN_TS" } | null {
  const built = buildTemplateCreatedDateFilter(
    value.templateDatePreset,
    value.templateDateFrom,
    value.templateDateTo,
  );
  const range = built?.createdDate?.value;
  if (!range) {
    return null;
  }
  const [startSec, endSec] = range.split(" - ").map((part) => Number(part.trim()));
  if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) {
    return null;
  }
  // unsubscribedAt is Date.now() milliseconds. The shared picker emits epoch seconds,
  // and that end second is truncated, so the inclusive end keeps its final millisecond.
  return {
    value: `${startSec * 1000} - ${endSec * 1000 + 999}`,
    comparisonType: "BETWEEN_TS",
  };
}

function unsubscribeSelectionId (row: any): string {
  const existing = String(row?.id ?? "").trim();
  if (existing) {
    return existing;
  }
  const recordId = String(row?.recordId ?? "").trim();
  const itemKind = String(row?.itemKind ?? "").trim();
  const itemValue = String(row?.itemValue ?? "").trim();
  if (
    !recordId ||
    (itemKind !== "category" && itemKind !== "event") ||
    !itemValue
  ) {
    return "";
  }
  return `${recordId}|${itemKind}|${itemValue}`;
}

function clauseValues (filter: Record<string, unknown> | null, field: string): Set<string> | null {
  const clause = filter?.[field] as { value?: unknown } | undefined;
  const v = clause?.value;
  return Array.isArray(v) && v.length > 0 ? new Set(v.map(String)) : null;
}

export default function UnsubscribedList (): React.ReactElement {
  const [events, setEvents] = useState<EventLookupOption[]>([]);
  const [firms, setFirms] = useState<IFirm[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [serverFilter, setServerFilter] = useState<Record<string, unknown>>({});
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const expandedRowsRef = useRef<UnsubscribedExpandedRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getEventLookupOptions(), getAllFirms(true)])
      .then(([eventData, firmData]) => {
        if (cancelled) {
          return;
        }
        setEvents((eventData ?? []).filter((event) => event.id));
        setFirms(Array.isArray(firmData) ? firmData : []);
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setFirms([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const eventTitleById = useMemo(() => {
    const eventMap = new Map<string, string>();
    events.forEach((event) => {
      if (event.id && event.title) {
        eventMap.set(event.id, event.title);
      }
    });
    return eventMap;
  }, [events]);

  const emailTypeFilterOptions = useMemo(
    () =>
      (Object.entries(EmailTypeMemberEnum) as [string, string][]).map(([key, label]) => ({
        id: key,
        name: label,
      })),
    []
  );

  const eventFilterOptions = useMemo(
    () =>
      events
        .filter((event) => event.id)
        .map((event) => ({
          id: String(event.id),
          name: formatEventLookupLabel(event),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [events]
  );

  const firmFilterOptions = useMemo(
    () =>
      firms
        .filter((f) => (f.name || f.firmName || "").trim() !== "")
        .map((f) => ({ id: f.id, name: (f.name || f.firmName || "").trim() })),
    [firms]
  );

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Date",
        fieldName: "unsubscribedAt",
        type: "custom",
        defaultCustomFilterValue: DEFAULT_TEMPLATE_DATE_FILTER,
        getCustomSelectedChips: unsubscribeDateFilterChips,
        render: ({ value, apply }) => (
          <TemplateDateFlightsPicker
            value={
              (value as TemplateDateFilterValue | null | undefined) ??
              DEFAULT_TEMPLATE_DATE_FILTER
            }
            onApply={(next) => {
              apply(next, unsubscribeDateClause(next));
            }}
            variant="catalog"
            label="By Unsubscribe Date:"
            dialogTitle="Unsubscribe date"
            showYearPresets={false}
            showLast3Days
            showLast7Days
            showLastWeek={false}
            labelClassName="font-semibold text-gray-font"
            triggerClassName="rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md"
            triggerInsetClassName="max-h-[0.875rem]"
          />
        ),
      },
      {
        name: "Firm",
        fieldName: "personFirm",
        type: "multiselect",
        objectData: firmFilterOptions,
        comparison: "EQUAL",
      },
      {
        name: "Email Type",
        fieldName: "unsubscribedCategories",
        type: "multiselect",
        objectData: emailTypeFilterOptions,
        comparison: "ARRAY_CONTAINS",
      },
      {
        name: "Event",
        fieldName: "unsubscribedEventIds",
        type: "multiselect",
        objectData: eventFilterOptions,
        comparison: "ARRAY_CONTAINS",
      },
    ],
    [emailTypeFilterOptions, eventFilterOptions, firmFilterOptions]
  );

  const unsubscribedAtToDate = useCallback((ts: number | undefined): Date | null => {
    if (ts == null) return null;
    return new Date(typeof ts === "number" && ts < 1e12 ? ts * 1000 : ts);
  }, []);

  const firmNameById = useMemo(() => {
    const m = new Map<string, string>();
    firms.forEach((f) => {
      const name = (f.name || f.firmName || "").trim();
      if (f.id && name) m.set(f.id, name);
    });
    return m;
  }, [firms]);

  const categoryFilter = useMemo(
    () => clauseValues(serverFilter, "unsubscribedCategories"),
    [serverFilter]
  );
  const eventFilter = useMemo(
    () => clauseValues(serverFilter, "unsubscribedEventIds"),
    [serverFilter]
  );

  const formatData = useCallback(
    (rows: any[]) => {
      if (!rows || !Array.isArray(rows)) {
        expandedRowsRef.current = [];
        return [];
      }
      const expanded: UnsubscribedExpandedRow[] = [];
      let groupIndex = 0;
      for (const row of rows) {
        groupIndex += 1;
        const date = unsubscribedAtToDate(row.unsubscribedAt);
        const unsubscribedDateStr = date ? formatDateToString(date) : "";
        const personFirmNameResolved =
          row.personFirmName ?? (row.personFirm ? firmNameById.get(row.personFirm) : undefined) ?? "";
        const contactIdVal = row.contactId ?? row.personShortId ?? row.personId ?? "";
        const categories = Array.isArray(row.unsubscribedCategories) ? row.unsubscribedCategories : [];
        const eventIds = Array.isArray(row.unsubscribedEventIds) ? row.unsubscribedEventIds : [];
        const showCategories = !eventFilter || !!categoryFilter;
        const showEvents = !categoryFilter || !!eventFilter;
        const categoriesToShow = showCategories
          ? categoryFilter ? categories.filter((c: string) => categoryFilter.has(c)) : categories
          : [];
        const eventsToShow = showEvents
          ? eventFilter ? eventIds.filter((eid: string) => eventFilter.has(eid)) : eventIds
          : [];
        const baseRow = {
          groupIndex,
          personId: row.personId,
          personName: row.personName,
          personTitle: row.personTitle,
          personFirm: row.personFirm,
          personFirmName: personFirmNameResolved,
          personShortId: row.personShortId,
          contactId: contactIdVal,
          emailMeeting: row.emailMeeting ?? "",
          unsubscribedDateStr,
          unsubscribedAt: unsubscribedDateStr,
        };
        for (const c of categoriesToShow) {
          expanded.push({
            id: `${row.id}-category-${c}`,
            recordId: row.id,
            itemKind: "category",
            itemValue: c,
            itemLabel: EmailTypeMemberEnum[c as keyof typeof EmailTypeMemberEnum] || c,
            ...baseRow,
          });
        }
        for (const eid of eventsToShow) {
          expanded.push({
            id: `${row.id}-event-${eid}`,
            recordId: row.id,
            itemKind: "event",
            itemValue: eid,
            itemLabel: eventTitleById.get(eid) || eid,
            ...baseRow,
          });
        }
      }
      expandedRowsRef.current = expanded;
      return expanded;
    },
    [eventTitleById, unsubscribedAtToDate, categoryFilter, eventFilter, firmNameById]
  );

  const columnsDef: IHeader[] = useMemo(
    () => [
      {
        id: "personName",
        header: "Name",
        cell: ({ row }) =>
          row.personId ? (
            <Link
              to={`/unsubscribed-list/${encodeURIComponent(row.personId)}`}
              className="text-secondary hover:underline"
            >
              {row.personName ?? "—"}
            </Link>
          ) : (
            <span>{row.personName ?? "—"}</span>
          ),
      },
      {
        id: "personTitle",
        header: "Title",
        cell: ({ row }) => <span>{row.personTitle ?? "—"}</span>,
      },
      {
        id: "personFirmName",
        header: "Firm",
        cell: ({ row }) => (
          <span>{row.personFirmName ?? row.personFirm ?? "—"}</span>
        ),
      },
      {
        id: "contactId",
        header: "Contact ID",
        cell: ({ row }) => (
          <span>{row.contactId ?? row.personShortId ?? row.personId ?? "—"}</span>
        ),
      },
      {
        id: "itemLabel",
        header: "Email/Meeting",
        // enableSorting: false,
        cell: ({ row }) => <span>{row.itemLabel ?? row.emailMeeting ?? "—"}</span>,
      },
      {
        id: "unsubscribedAt",
        header: "Unsubscribe Date",
        cell: ({ row }) => <span>{row.unsubscribedDateStr ?? "—"}</span>,
      },
    ],
    []
  );

  const rowSelection = useMemo(
    () => ({
      selectedIds,
      onToggleRow: (id: string, selected: boolean) => {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (selected) {
            next.add(id);
          } else {
            next.delete(id);
          }
          return next;
        });
      },
      onTogglePage: (pageRowIds: string[], selectAll: boolean) => {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (selectAll) {
            pageRowIds.forEach((id) => next.add(id));
          } else {
            pageRowIds.forEach((id) => next.delete(id));
          }
          return next;
        });
      },
      getRowId: (row: any) => unsubscribeSelectionId(row),
      isRowSelectable: (row: any) => unsubscribeSelectionId(row) !== "",
      headerLabel: "Subscribe",
    }),
    [selectedIds]
  );

  const someSelected = selectedIds.size > 0;

  const openSubscribeModal = useCallback(() => {
    if (selectedIds.size > 0) {
      setSubscribeModalOpen(true);
    }
  }, [selectedIds.size]);

  const closeSubscribeModal = useCallback(() => {
    setSubscribeModalOpen(false);
  }, []);

  const handleConfirmSubscribe = useCallback(async () => {
    if (selectedIds.size === 0) {
      return;
    }
    const fromExpanded = expandedRowsRef.current.filter(
      (r) => r.id && selectedIds.has(r.id)
    );
    const covered = new Set(fromExpanded.map((row) => row.id));
    const fromSelection = Array.from(selectedIds)
      .filter((id) => !covered.has(id))
      .map((id) => {
        const [recordId, itemKind, ...valueParts] = id.split("|");
        const itemValue = valueParts.join("|");
        if (
          !recordId ||
          (itemKind !== "category" && itemKind !== "event") ||
          !itemValue
        ) {
          return null;
        }
        return {
          id,
          recordId,
          itemKind: itemKind as "category" | "event",
          itemValue,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row != null);
    const toSubscribe = [...fromExpanded, ...fromSelection];
    if (toSubscribe.length === 0) {
      return;
    }
    setSubscribing(true);
    try {
      await Promise.all(
        toSubscribe.map((row) =>
          subscribeEmailUnsubscriberItem({
            id: row.recordId,
            removeCategory: row.itemKind === "category" ? row.itemValue : undefined,
            removeEventId: row.itemKind === "event" ? row.itemValue : undefined,
          })
        )
      );
      closeSubscribeModal();
      setSelectedIds(new Set());
      setTableRefreshKey((k) => k + 1);
    } finally {
      setSubscribing(false);
    }
  }, [selectedIds, closeSubscribeModal]);

  const subscribeAccessory = (
    <Button
      text={subscribing ? "Subscribing…" : "Subscribe"}
      color="primary"
      variant="filled"
      size="fixed"
      disabled={!someSelected || subscribing}
      onClick={openSubscribeModal}
    />
  );

  return (
    <main className="text-left">
      <Modal
        className="!text-Default py-6 px-6 md:py-8 md:px-8 lg:py-10 lg:px-10"
        isModalOpen={subscribeModalOpen}
        toggleModal={closeSubscribeModal}
        title="Subscribe"
        titleColor="text-Default"
      >
        <div className="flex flex-col gap-6 min-w-[26rem] sm:min-w-[32rem]">
          <p className="text-base text-gray-font">
            Are you sure you want to subscribe this/these professional(s) to the selected emails?
          </p>
          <div className="flex flex-col gap-4 w-full sm:flex-row sm:justify-end sm:w-auto">
            <NewButton
              type="button"
              onClick={closeSubscribeModal}
              content="textIcon"
              size="small"
              style="outlined"
              color="neutral"
              icon={<CloseModal /> as JSX.Element}
              text="No, cancel"
              className="w-full sm:w-auto"
            />
            <NewButton
              type="button"
              onClick={handleConfirmSubscribe}
              content="text"
              size="small"
              style="filled"
              color="accent"
              text={subscribing ? "Subscribing…" : "Yes, subscribe"}
              disabled={subscribing}
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </Modal>

      <FilteredTableCore
        key={`${TABLE_KEY}-${tableRefreshKey}`}
        showRowNumber
        tableKey={TABLE_KEY}
        columnsDef={columnsDef}
        endpoint={endpoint}
        filtersData={filtersData}
        formatData={formatData}
        rowSelection={rowSelection}
        selectionInsertAfterDataColumns={7}
        persistSorting={false}
        defaultSorting={{ field: "unsubscribedAt", order: "desc" }}
        showExcelExport
        showColumnVisibility={false}
        showSearchInput
        searchPlaceholder="Search by Name, Last Name, Email or Contact ID"
        excelFilename="Unsubscribed.xlsx"
        searchToolbarAccessory={subscribeAccessory}
        onQueryFilterChange={setServerFilter}
      />
    </main>
  );
}
